// RequestAgent.mock.tsx
import React, { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import format from 'date-fns/format'
import { FiExternalLink, FiRefreshCcw, FiFilter, FiX, FiFileText, FiAlertCircle } from 'react-icons/fi'
import { DialogUploadRequest, StatusServicesRequestEnum, DocumentRequirement } from './DialogUploadRequest'
import { AxiosHeaders, AxiosRequestHeaders } from 'axios'
import { useAppSelector } from '../../../hooks/hooksRedux'
import { RootState } from '../../../store'
import { defaultColors } from '../../../styles/style-guide'
import { PageHeadline } from '../../../components/atoms'

interface Shareholder { id: string; name: string }
interface ServiceRequest {
  id: string
  first_name: string
  company_name: string
  email: string
  created_at: string
  value: number
  registration_number?: string
  title: string
  statusEnum: StatusServicesRequestEnum
  status: string
  shareholders?: Shareholder[]
}

// Mock de requests
const mockRequests: ServiceRequest[] = [
  {
    id: '1',
    first_name: 'Alice',
    company_name: 'Acme Ltd.',
    email: 'alice@acme.com',
    created_at: new Date().toISOString(),
    value: 1750,
    registration_number: '12345',
    title: 'Portal de Notícias',
    statusEnum: StatusServicesRequestEnum.PENDING_UPLOAD_FILE,
    status: 'PENDING_UPLOAD_FILE',
    shareholders: [
      { id: 'sh1', name: 'Bob' },
      { id: 'sh2', name: 'Carol' }
    ]
  },
  {
    id: '2',
    first_name: 'Jean',
    company_name: 'Example SA',
    email: 'jean@example.com',
    created_at: '2025-02-10T10:00:00.000Z',
    value: 315.5,
    registration_number: '67890',
    title: 'Loja Online',
    statusEnum: StatusServicesRequestEnum.COMPLETED,
    status: 'COMPLETED',
    shareholders: []
  }
]

const STATUS_OPTIONS = [
  { value: 'PENDING_UPLOAD_FILE', labelKey: 'labels.lblPendingUploadFile', fallback: 'Pending Upload File' },
  { value: 'COMPLETED', labelKey: 'labels.lblCompleted', fallback: 'Completed' }
] as const

interface FilterOptions {
  status: string
  companyName: string
  email: string
}

export const RequestAgent: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data)
  const primaryColor = colors?.primary || defaultColors.primary

  // Estados
  const [requests, setRequests] = useState<ServiceRequest[]>(mockRequests)
  const [requiredDocs, setRequiredDocs] = useState<Record<string, DocumentRequirement[]>>({})
  const [openDialog, setOpenDialog] = useState(false)
  const [activeId, setActiveId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filtros
  const [status, setStatus] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')

  const filters = useMemo(() => ({
    status,
    companyName,
    email
  }), [status, companyName, email])

  const headers: AxiosRequestHeaders = new AxiosHeaders({
    Authorization: 'Bearer MOCK_TOKEN',
    'Content-Language': 'pt-BR'
  })

  const formatterDate = (d: string) => format(new Date(d), 'MM/dd/yyyy')
  const formatterValue = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: 'USD'
      }),
    [i18n.language]
  )

  // Gera a lista de docs
  const getDocsConfig = (req: ServiceRequest): DocumentRequirement[] => {
    const base: DocumentRequirement[] = [
      { key: 'cert_incorp', label: t('Certificate of Incorporation'), required: true, signatory: null },
      { key: 'memorandum',  label: t('Memorandum of Association'),  required: true, signatory: null },
      { key: 'first_dir_res', label: t('First Directors Resolution'), required: false, signatory: 'agent' },
      { key: 'reg_of_dirs',  label: t('Register of Directors'),     required: true, signatory: 'director' },
      { key: 'reg_of_mem',   label: t('Register of Members'),       required: true, signatory: 'shareholder' },
    ]

    const sharesDocs = (req.shareholders ?? []).map<DocumentRequirement>(member => ({
      key:       `certificate_of_shares_${member.id}`,
      label:     `${t('Certificate of Shares')} – ${member.name}`,
      required:  true,
      signatory: 'shareholder'
    }))

    return [
      ...base.slice(0, 5),
      ...sharesDocs,
      ...base.slice(5)
    ]
  }

  // Abrir modal de upload
  const openUpload = (id: string) => {
    setActiveId(id)
    setRequiredDocs(prev => ({
      ...prev,
      [id]: getDocsConfig(requests.find(r => r.id === id)!)
    }))
    setOpenDialog(true)
  }

  // Simula "aprovar empresa"
  const canApprove = () => {
    const docs = requiredDocs[activeId] ?? []
    return docs.every(d => d.status === StatusServicesRequestEnum.COMPLETED)
  }

  const handleApprove = () => {
    if (!canApprove()) return
    setRequests(rs =>
      rs.map(r =>
        r.id === activeId
          ? { ...r, statusEnum: StatusServicesRequestEnum.COMPLETED, status: 'COMPLETED' }
          : r
      )
    )
    setOpenDialog(false)
  }

  const handleDocumentsChange = useCallback(
    (docs: DocumentRequirement[]) => {
      if (!activeId) return
      setRequiredDocs(prev => ({
        ...prev,
        [activeId]: docs
      }))
    },
    [activeId]
  )

  // Handlers dos filtros
  const handleStatusChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.target.value)
  }, [])

  const handleCompanyNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyName(event.target.value)
  }, [])

  const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }, [])

  const handleClearFilters = useCallback(() => {
    setStatus('')
    setCompanyName('')
    setEmail('')
  }, [])

  const handleRefresh = useCallback(async () => {
    setLoading(true)
    // Simula refresh
    await new Promise(resolve => setTimeout(resolve, 1000))
    setLoading(false)
  }, [])

  // Filtra os requests baseado nos filtros ativos
  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      if (status && req.status !== status) return false
      if (companyName && !req.company_name.toLowerCase().includes(companyName.toLowerCase())) return false
      if (email && !req.email.toLowerCase().includes(email.toLowerCase())) return false
      return true
    })
  }, [requests, status, companyName, email])

  // Renderiza estados especiais
  const renderError = () => (
    <div className="flex items-center justify-center py-8 text-red-600" role="alert">
      <FiAlertCircle className="mr-2" size={20} />
      <span>{error}</span>
      <button 
        onClick={handleRefresh}
        className="ml-4 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
      >
        {t('actions.retry') || 'Retry'}
      </button>
    </div>
  )

  const renderLoading = () => (
    <div className="text-center py-8" role="status" aria-live="polite">
      <div className="inline-flex items-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mr-3"></div>
        {t('labels.lblLoading') || 'Loading'}...
      </div>
    </div>
  )

  const renderEmptyState = () => (
    <tr>
      <td colSpan={8} className="text-center py-8 text-gray-500">
        <div className="flex flex-col items-center">
          <FiFileText size={48} className="mb-4 text-gray-300" />
          <p>{t('labels.lblNoRecords') || 'No records found'}</p>
        </div>
      </td>
    </tr>
  )

  const renderRequestRow = (req: ServiceRequest) => (
    <tr key={req.id} className="even:bg-gray-50 hover:bg-gray-100 transition-colors">
      <td className="px-3 py-3 border">
        {req.first_name}
      </td>
      <td className="px-3 py-3 border">
        {req.company_name}
      </td>
      <td className="px-3 py-3 border">
        {req.email}
      </td>
      <td className="px-3 py-3 border">
        {formatterDate(req.created_at)}
      </td>
      <td className="px-3 py-3 border font-medium">
        {formatterValue.format(req.value)}
      </td>
      <td className="px-3 py-3 border">
        {req.registration_number || '-'}
      </td>
      <td className="px-3 py-3 border">
        {req.title}
      </td>
      <td className="px-3 py-3 border">
        {req.statusEnum === StatusServicesRequestEnum.PENDING_UPLOAD_FILE ? (
          <button
            className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
            onClick={() => openUpload(req.id)}
          >
            <FiExternalLink className="mr-1" size={14} />
            {t('labels.lblPendingUploadFile') || req.status}
          </button>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {t('labels.lblCompleted') || req.status}
          </span>
        )}
      </td>
    </tr>
  )

  return (
    <>
      <DialogUploadRequest
        openDialog={openDialog}
        onOpenDialogChange={setOpenDialog}
        idServicesRequest={activeId}
        fetchHeaders={headers}
        requiredDocuments={requiredDocs[activeId] ?? []}
        onDocumentsChange={handleDocumentsChange}
        onApprove={handleApprove}
        canApprove={canApprove()}
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="mb-6">
          <PageHeadline
            title={t('OnboardingRequestList') || 'Onboarding Request List'}
            actions={
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleClearFilters}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  {t('actions.clearFilters') || 'Clear Filters'}
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FiRefreshCcw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  {t('actions.refresh') || 'Refresh'}
                </button>
              </div>
            }
          />
        </div>

        {/* Filtros */}
        <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label htmlFor="status-select" className="block text-sm font-medium text-gray-700">
              {t('CLStatus') || 'Status'}
            </label>
            <select
              id="status-select"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={status}
              onChange={handleStatusChange}
            >
              <option value="">{t('labels.lblSelectStatus') || 'Select Status'}</option>
              {STATUS_OPTIONS.map(({ value, labelKey, fallback }) => (
                <option key={value} value={value}>
                  {t(labelKey) || fallback}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="company-name-input" className="block text-sm font-medium text-gray-700">
              {t('CLCompanyName') || 'Company Name'}
            </label>
                  <input
              id="company-name-input"
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={companyName}
              onChange={handleCompanyNameChange}
              placeholder={t('labels.lblSearchCompany') || 'Search company...'}
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="email-input" className="block text-sm font-medium text-gray-700">
              {t('CLEmail') || 'Email'}
            </label>
                  <input
              id="email-input"
              type="email"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={email}
              onChange={handleEmailChange}
              placeholder={t('labels.lblSearchEmail') || 'Search email...'}
            />
          </div>
        </section>

        {/* Filtros Ativos */}
        {(status || companyName || email) && (
          <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-blue-900">
                {t('messages.filterActive') || 'Active Filters'}:
              </h3>
              <button
                onClick={handleClearFilters}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                {t('actions.clearFilters') || 'Clear Filters'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {status && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {t('CLStatus') || 'Status'}:{' '}
                  {(() => {
                    const option = STATUS_OPTIONS.find(s => s.value === status);
                    return option ? t(option.labelKey) || option.fallback : status;
                  })()}
                  <button
                    onClick={() => setStatus('')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    aria-label={t('actions.removeFilter') || 'Remove filter'}
                  >
                    <FiX size={12} />
                  </button>
                </span>
              )}
              {companyName && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {t('CLCompanyName') || 'Company Name'}: {companyName}
                  <button
                    onClick={() => setCompanyName('')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    aria-label={t('actions.removeFilter') || 'Remove filter'}
                  >
                    <FiX size={12} />
                  </button>
                </span>
              )}
              {email && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {t('CLEmail') || 'Email'}: {email}
                  <button
                    onClick={() => setEmail('')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    aria-label={t('actions.removeFilter') || 'Remove filter'}
                  >
                    <FiX size={12} />
                  </button>
                </span>
              )}
            </div>
          </section>
        )}

        {/* Tabela de dados */}
        <section className="overflow-x-auto" role="region" aria-labelledby="request-agent-title">
          {error ? (
            renderError()
          ) : loading ? (
            renderLoading()
          ) : (
            <table className="min-w-full border-collapse text-sm" role="table">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLName') || 'Name'}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLCompanyName') || 'Company Name'}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLEmail') || 'Email'}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLCreated') || 'Created'}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLValue') || 'Value'}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLNumberRegister') || 'Registration Number'}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLTitle') || 'Title'}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                    {t('CLStatus') || 'Status'}
                  </th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {filteredRequests.length > 0 ? filteredRequests.map(renderRequestRow) : renderEmptyState()}
              </tbody>
            </table>
          )}
        </section>

        {/* Informações de resultados */}
        {filteredRequests.length > 0 && (
          <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
            <div>
              {t('messages.showingResults') || 'Showing'} {filteredRequests.length} {t('messages.results') || 'results'}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
